import React, { useCallback, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
	Eye,
	Frown,
	Globe2,
	Lightbulb,
	Pause,
	RotateCcw,
	Smile,
	SmilePlus,
} from "lucide-react";
import BackdropAlert from "components/Custom/BackdropAlert";

const ratingOptions = [
	{ rating: "again", label: "Otra vez", shortcut: "1", icon: RotateCcw, color: "#ef4444" },
	{ rating: "hard", label: "Difícil", shortcut: "2", icon: Frown, color: "#f97316" },
	{ rating: "good", label: "Bien", shortcut: "3", icon: Smile, color: "#0ea5e9" },
	{ rating: "easy", label: "Fácil", shortcut: "4", icon: SmilePlus, color: "#10b981" },
];

function isTypingTarget(target) {
	const tagName = target?.tagName?.toLowerCase();

	return Boolean(
		target?.isContentEditable ||
			tagName === "input" ||
			tagName === "textarea" ||
			tagName === "select"
	);
}

function capitalizeFirstLetter(text = "") {
	if (!text) {
		return "";
	}

	return text.charAt(0).toUpperCase() + text.slice(1);
}

function InfoBadge({ children, icon: Icon, color = "#2563eb" }) {
	return (
		<Box
			component="span"
			sx={{
				alignItems: "center",
				backgroundColor: "#eff6ff",
				borderRadius: "999px",
				color,
				display: "inline-flex",
				fontSize: 12,
				fontWeight: 700,
				gap: 0.75,
				lineHeight: 1,
				px: 1.25,
				py: 0.75,
			}}
		>
			{Icon && <Icon size={14} strokeWidth={2.4} />}
			{children}
		</Box>
	);
}

function StateBadge({ showAnswer }) {
	return (
		<Box
			component="span"
			sx={{
				alignItems: "center",
				backgroundColor: showAnswer ? "#ecfdf5" : "#eff6ff",
				borderRadius: "999px",
				color: showAnswer ? "#047857" : "#2563eb",
				display: "inline-flex",
				fontSize: 12,
				fontWeight: 700,
				gap: 0.75,
				lineHeight: 1,
				px: 1.25,
				py: 0.75,
			}}
		>
			<Box
				component="span"
				sx={{
					backgroundColor: showAnswer ? "#10b981" : "#2563eb",
					borderRadius: "50%",
					height: 8,
					width: 8,
				}}
			/>
			{showAnswer ? "Reverso" : "Frente"}
		</Box>
	);
}

function RatingButton({ option, disabled, onClick }) {
	const Icon = option.icon;

	return (
		<Button
			variant="outlined"
			disabled={disabled}
			onClick={() => onClick(option.rating)}
			sx={{
				borderColor: "#dfe5ee",
				borderRadius: "10px",
				boxShadow: "none",
				color: "#111827",
				height: 56,
				justifyContent: "space-between",
				minWidth: 0,
				px: 2,
				textTransform: "none",
				"&:hover": {
					backgroundColor: "#f8fafc",
					borderColor: option.color,
				},
			}}
		>
			<Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
				<Icon size={22} color={option.color} strokeWidth={2.2} style={{ flexShrink: 0 }} />
				<Typography
					component="span"
					sx={{ fontSize: { xs: 13, sm: 14 }, fontWeight: 700, whiteSpace: "nowrap" }}
				>
					{option.label}
				</Typography>
			</Stack>
			<Box
				component="span"
				sx={{
					alignItems: "center",
					backgroundColor: "#f8fafc",
					border: "1px solid #dfe5ee",
					borderRadius: "8px",
					color: "#475569",
					display: "inline-flex",
					fontSize: 13,
					fontWeight: 800,
					height: 28,
					justifyContent: "center",
					minWidth: 28,
				}}
			>
				{option.shortcut}
			</Box>
		</Button>
	);
}

export default function FlashcardsBody({ data, onReview, onSuspend, onFinish, loading = false }) {
	const [showAnswer, setShowAnswer] = useState(false);
	const [isHintExpanded, setIsHintExpanded] = useState(false);
	const [showSuspendAlert, setShowSuspendAlert] = useState(false);
	const [startedAt, setStartedAt] = useState(Date.now());

	const flashcard = data?.flashcard || {};
	const specialty = data?.specialty;
	const topic = data?.topic;
	const isGlobal = Boolean(flashcard.is_global);
	const cardText = capitalizeFirstLetter(showAnswer ? flashcard.back : flashcard.front);
	const hintText = String(flashcard.hint || "");
	const canExpandHint = hintText.length > 140;

	useEffect(() => {
		setShowAnswer(false);
		setIsHintExpanded(false);
		setStartedAt(Date.now());
	}, [flashcard.id]);

	const handleReview = useCallback((rating) => {
		const responseTimeSeconds = Math.max(1, Math.round((Date.now() - startedAt) / 1000));
		onReview?.(rating, responseTimeSeconds);
	}, [onReview, startedAt]);

	const handleConfirmSuspend = () => {
		setShowSuspendAlert(false);
		onSuspend?.();
	};

	useEffect(() => {
		function handleKeyDown(event) {
			if (loading || showSuspendAlert || isTypingTarget(event.target) || event.repeat) {
				return;
			}

			if (event.code === "Space") {
				event.preventDefault();
				setShowAnswer((prev) => !prev);
				return;
			}

			const selectedOption = ratingOptions.find((option) => option.shortcut === event.key);
			if (selectedOption && showAnswer) {
				event.preventDefault();
				handleReview(selectedOption.rating);
				return;
			}

			if (event.key.toLowerCase() === "s") {
				event.preventDefault();
				setShowSuspendAlert(true);
				return;
			}

			if (event.key.toLowerCase() === "f") {
				event.preventDefault();
				onFinish?.();
			}
		}

		window.addEventListener("keydown", handleKeyDown);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
		};
	}, [handleReview, loading, onFinish, showAnswer, showSuspendAlert]);

	if (!data?.flashcard) {
		return (
			<Paper
				variant="outlined"
				sx={{ borderColor: "#dfe5ee", borderRadius: "12px", p: 4, textAlign: "center" }}
			>
				<Typography sx={{ color: "#64748b", fontWeight: 600 }}>
					Cargando flashcard...
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper
			variant="outlined"
			sx={{
				borderColor: "#dfe5ee",
				borderRadius: "12px",
				boxShadow: "0 10px 30px rgba(15, 23, 42, 0.04)",
				p: { xs: 2, md: 2.5 },
				width: "100%",
			}}
		>
			<Paper
				variant="outlined"
				sx={{
					animation: showAnswer
						? "flashcardFlipToBack 360ms ease-out"
						: "flashcardFlipToFront 360ms ease-out",
					backfaceVisibility: "hidden",
					backgroundColor: showAnswer ? "#f8fffb" : "#ffffff",
					borderColor: showAnswer ? "#86efac" : "#e5e7eb",
					borderRadius: "12px",
					boxShadow: showAnswer
						? "0 10px 24px rgba(16, 185, 129, 0.12)"
						: "0 10px 20px rgba(15, 23, 42, 0.05)",
					p: { xs: 2, md: 3 },
					transformOrigin: "center center",
					transformStyle: "preserve-3d",
					"@keyframes flashcardFlipToBack": {
						"0%": { opacity: 0.42, transform: "rotateY(-92deg) scale(0.98)" },
						"100%": { opacity: 1, transform: "rotateY(0deg) scale(1)" },
					},
					"@keyframes flashcardFlipToFront": {
						"0%": { opacity: 0.42, transform: "rotateY(92deg) scale(0.98)" },
						"100%": { opacity: 1, transform: "rotateY(0deg) scale(1)" },
					},
				}}
			>
				<Box
					sx={{
						alignItems: "center",
						display: "flex",
						justifyContent: "space-between",
						position: "relative",
					}}
				>
					<StateBadge showAnswer={showAnswer} />
					{showAnswer && (
						<Typography
							component="span"
							sx={{
								color: "#047857",
								display: { xs: "none", sm: "inline" },
								fontSize: 12,
								fontWeight: 800,
								left: "50%",
								letterSpacing: 0.8,
								position: "absolute",
								textTransform: "uppercase",
								transform: "translateX(-50%)",
								whiteSpace: "nowrap",
							}}
						>
							Respuesta
						</Typography>
					)}
					<Box sx={{ minWidth: 0 }}>{isGlobal && <InfoBadge icon={Globe2}>Flashcard global</InfoBadge>}</Box>
				</Box>

				<Stack spacing={2.5} alignItems="center" sx={{ my: { xs: 4, md: 5 } }}>
					<Typography
						component="h2"
						sx={{
							alignSelf: "center",
							color: "#0f172a",
							fontSize: { xs: 20, md: 26 },
							fontWeight: 700,
							lineHeight: 1.45,
							maxWidth: 860,
							mx: "auto",
							textAlign: "center",
							width: "100%",
						}}
					>
						{cardText}
					</Typography>

					{(specialty || topic) && (
						<Typography sx={{ color: "#64748b", fontSize: 13, fontWeight: 600, textAlign: "center" }}>
							{specialty?.name}{specialty?.section && ` · ${specialty.section}`}{topic?.name && ` · ${topic.name}`}
						</Typography>
					)}
				</Stack>

				{flashcard.hint && !showAnswer && (
					<Box
						onClick={() => {
							if (canExpandHint) setIsHintExpanded((expanded) => !expanded);
						}}
						sx={{
							alignItems: { xs: "flex-start", sm: "center" },
							border: "1px solid #dfe5ee",
							borderRadius: "10px",
							color: "#475569",
							display: "flex",
							fontSize: 13,
							gap: 1,
							justifyContent: "center",
							mx: "auto",
							maxWidth: 640,
							px: 2,
							py: 1.25,
							textAlign: "center",
							width: "100%",
							cursor: "default",
						}}
					>
						<Box component="span" sx={{ display: "inline-flex", mr: 1, verticalAlign: "middle" }}>
							<Lightbulb size={17} color="#7395ff" strokeWidth={2.1} />
						</Box>
						<Box
							component="span"
							sx={{
								display: "block",
								flex: 1,
								minWidth: 0,
								textAlign: { xs: "left", sm: "center" },
								userSelect: "none",
							}}
						>
							<Box
								component="span"
								sx={{
									display: { xs: isHintExpanded || !canExpandHint ? "inline" : "-webkit-box", sm: "inline" },
									overflow: { xs: isHintExpanded || !canExpandHint ? "visible" : "hidden", sm: "visible" },
									...(isHintExpanded || !canExpandHint ? {} : {
										WebkitBoxOrient: "vertical",
										WebkitLineClamp: 2,
									}),
								}}
							>
								<Box component="span" sx={{ fontWeight: 700 }}>Pista:</Box>{" "}{hintText}{" "}
							</Box>
							{canExpandHint && (
								<Button
									size="small"
									sx={{
										display: { xs: "inline", sm: "none" },
										fontSize: 12,
										fontWeight: 700,
										lineHeight: 1.2,
										minWidth: "auto",
										p: 0,
										textTransform: "none",
										verticalAlign: "baseline",
									}}
								>
									{isHintExpanded ? "Ver menos" : "Ver más"}
								</Button>
							)}
						</Box>
					</Box>
				)}
			</Paper>

			<Stack alignItems="center" spacing={2} sx={{ mt: 2.5 }}>
				<Button
					variant="contained"
					disabled={loading}
					onClick={() => setShowAnswer((prev) => !prev)}
					sx={{
						backgroundColor: "#FE8C62",
						borderRadius: "10px",
						boxShadow: "0 10px 18px rgba(254, 140, 98, 0.26)",
						fontWeight: 800,
						minHeight: 42,
						minWidth: { xs: "100%", sm: 320, md: 292 },
						px: 2.5,
						py: 0.85,
						textTransform: "none",
						"&:hover": { backgroundColor: "#f97316" },
						"&:focus": { outline: "none" },
						"&:focus-visible": { outline: "none", boxShadow: "0 10px 18px rgba(254, 140, 98, 0.26)" },
					}}
				>
					<Box
						component="span"
						sx={{
							alignItems: "center",
							display: "flex",
							gap: 1.25,
							justifyContent: "center",
							position: "relative",
							width: "100%",
						}}
					>
						<Box
							component="span"
							sx={{
								alignItems: "center",
								display: "inline-flex",
								"& svg": {
									height: { xs: 22, sm: 17 },
									width: { xs: 22, sm: 17 },
								},
							}}
						>
							<Eye size={17} />
						</Box>
						<Box component="span">{showAnswer ? "Ocultar respuesta" : "Mostrar respuesta"}</Box>
						<Box
							component="span"
							sx={{
								alignItems: "center",
								border: "1px solid rgba(255, 255, 255, 0.32)",
								borderRadius: "7px",
								display: "inline-flex",
								fontSize: 11,
								fontWeight: 800,
								height: 24,
								justifyContent: "center",
								lineHeight: 1,
								position: { xs: "static", sm: "absolute" },
								right: 0,
								px: 1,
							}}
						>
							Espacio
						</Box>
					</Box>
				</Button>

				<Divider sx={{ width: "100%" }}>
					<Typography sx={{ color: "#64748b", fontSize: 12, fontWeight: 700, px: 2 }}>
						Después de ver la respuesta, califica tu recuerdo
					</Typography>
				</Divider>

				<Box
					sx={{
						display: "grid",
						gap: 1.5,
						gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
						width: "100%",
					}}
				>
					{ratingOptions.map((option) => (
						<RatingButton
							key={option.rating}
							option={option}
							disabled={!showAnswer || loading}
							onClick={handleReview}
						/>
					))}
				</Box>

				<Button
					variant="outlined"
					disabled={loading}
					onClick={() => setShowSuspendAlert(true)}
					startIcon={<Pause size={17} />}
					sx={{
						borderColor: "#dfe5ee",
						borderRadius: "10px",
						color: "#334155",
						fontWeight: 700,
						minWidth: { xs: "100%", sm: 220 },
						textTransform: "none",
					}}
				>
					Suspender tarjeta
				</Button>
			</Stack>

			<BackdropAlert
				open={showSuspendAlert}
				onClose={() => setShowSuspendAlert(false)}
				severity="warning"
				title="Suspender tarjeta"
				message="¿Quieres suspender esta tarjeta? Ya no aparecerá en tus sesiones futuras."
				primaryAction={{
					label: "Suspender tarjeta",
					onClick: handleConfirmSuspend,
					color: "warning",
				}}
				secondaryAction={{
					label: "Cancelar",
					onClick: () => setShowSuspendAlert(false),
				}}
			/>
		</Paper>
	);
}
